/**
 * RestConflictModal
 *
 * Aviso não-bloqueante de conflito de descanso (regra das 48h) ao trocar o
 * dia de um treino — ver app/api/user_workout_plans.py::_detect_rest_conflicts.
 * O aluno pode confirmar e seguir mesmo assim; isso só torna a decisão
 * consciente em vez de acidental (substitui um window.confirm() nativo).
 *
 * Props:
 *   open       – bool
 *   message    – texto principal do aviso (vem do backend)
 *   conflicts  – [{ conflicting_title, same_day, shared_labels }]
 *   saving     – bool, desabilita os botões enquanto confirma
 *   onConfirm  – () => void
 *   onCancel   – () => void
 */
import { createPortal } from "react-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTriangleExclamation } from "@fortawesome/free-solid-svg-icons";
import styles from "./RestConflictModal.module.css";
import ModalHeader from "./ModalHeader";
import { useBodyScrollLock } from "../hooks/useBodyScrollLock";

export default function RestConflictModal({ open, message, conflicts = [], saving = false, onConfirm, onCancel }) {
  useBodyScrollLock(!!open);
  if (!open) return null;

  return createPortal(
    <div className={styles.overlay} onClick={onCancel}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
        <ModalHeader onClose={onCancel} disabled={saving} />

        <div className={styles.header}>
          <span className={styles.icon}><FontAwesomeIcon icon={faTriangleExclamation} /></span>
          <div>
            <h2 className={styles.title}>Conflito de descanso</h2>
            <p className={styles.subtitle}>{message}</p>
          </div>
        </div>

        <div className={styles.body}>
          {conflicts.map((c, i) => (
            <div key={i} className={styles.conflictItem}>
              <span className={styles.conflictTitle}>{c.conflicting_title}</span>
              <span className={styles.conflictMeta}>
                {c.same_day ? "mesmo dia" : "dia seguido"} · grupo em comum: {c.shared_labels?.join(", ")}
              </span>
            </div>
          ))}
          <p className={styles.hint}>
            O ideal é pelo menos 48h de descanso pro mesmo grupo muscular. Você pode confirmar a
            troca mesmo assim, ou escolher outro dia.
          </p>
        </div>

        <div className={styles.footer}>
          <button type="button" className={`${styles.btn} ${styles.btnSecondary}`} onClick={onCancel} disabled={saving}>
            Cancelar
          </button>
          <button type="button" className={`${styles.btn} ${styles.btnWarning}`} onClick={onConfirm} disabled={saving}>
            {saving ? "Confirmando…" : "Confirmar mesmo assim"}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
